import React, { useState, useEffect, useCallback } from 'react';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import OfferDocument from './OfferDocument';
import { Typography, Button, Card, Checkbox, Input, Divider, Select, Tag, Row, Col } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { calculateAnnualOperatingCosts } from './utils';
import mockData from '../mockData.json';

const { Title } = Typography;
const { Option } = Select;

const problemOptions = mockData.map((item) => item.Analys);

const colorMap = {};
const colors = ['magenta', 'red', 'volcano', 'orange', 'gold', 'lime', 'cyan', 'blue', 'purple', 'pink'];

problemOptions.forEach((problem, index) => {
  colorMap[problem] = colors[index % colors.length];
});

const initializeSelectedControllers = (models) => {
  const initialControllers = {};
  models.forEach((model) => {
    const modelId = model.Product && model.Modell ? `${model.Product}-${model.Modell}` : null;
    if (modelId && model.Styrenheter?.length === 1) {
      initialControllers[modelId] = model.Styrenheter[0].Artikelnummer;
    } else if (modelId && model.Styrenheter?.length > 1) {
      initialControllers[modelId] = model.Styrenheter[0].Artikelnummer;
    }
  });
  return initialControllers;
};

const initializeSelectedOptionals = (products) => {
  const initialOptionals = {};
  products.forEach(product => {
    product.Modeller.forEach((model, modelIndex) => {
      const modelId = `${product.Product}-${model.Modell}-${modelIndex}`;
      initialOptionals[modelId] = null;
    });
  });
  return initialOptionals;
};

const Quote = ({ products, likelyFlow, totalHardness, nitrateLevel, getModelFlowCapacity, analysisData, presentProblems }) => {
  const [selectedModelIds, setSelectedModelIds] = useState([]);
  const [selectedControllers, setSelectedControllers] = useState(() => initializeSelectedControllers(products.flatMap(product => product.Modeller)));
  const [selectedOptionals, setSelectedOptionals] = useState(() => initializeSelectedOptionals(products));
  const [customerInfo, setCustomerInfo] = useState({ name: '', address: '' });
  const [resellerInfo, setResellerInfo] = useState({ name: '', address: '' });

  useEffect(() => {
    const newControllers = initializeSelectedControllers(products.flatMap(product => product.Modeller));
    setSelectedControllers(newControllers);
    const newOptionals = initializeSelectedOptionals(products);
    setSelectedOptionals(newOptionals);
  }, [products]);

  const handleControllerChange = useCallback((modelId, controllerId) => {
    if (!modelId || modelId.includes("undefined")) {
      console.error("Invalid or undefined modelId detected:", modelId);
      return;
    }
    setSelectedControllers(current => ({ ...current, [modelId]: controllerId }));
  }, []);

  useEffect(() => {
    products.forEach((product) => {
      product.Modeller.forEach((model, mIndex) => {
        const modelId = `${product.Product}-${model.Modell}-${mIndex}`;
        if (model.Styrenheter && Array.isArray(model.Styrenheter) && !selectedControllers[modelId] && model.Styrenheter.length > 0) {
          handleControllerChange(modelId, model.Styrenheter[0].Artikelnummer);
        }
      });
    });
  }, [products, selectedControllers, handleControllerChange]);

  const handleControllerSelection = (modelId, controllerId) => {
    setSelectedControllers(current => ({ ...current, [modelId]: controllerId }));
  };

  const handleOptionalSelection = (modelId, optionalId) => {
    setSelectedOptionals(current => ({ ...current, [modelId]: optionalId }));
  };

  if (!products || !Array.isArray(products)) {
    console.error('Products is not an array or undefined:', products);
    return <Typography type="danger">Fel: Produktdata är ogiltig eller har inte laddats.</Typography>;
  }

  const totalSum = products.reduce((acc, product) => {
    const productPriceSum = product.Modeller.reduce((modelSum, model) => {
      const modelId = `${product.Product}-${model.Modell}-${product.Modeller.indexOf(model)}`;

      if (selectedModelIds.includes(modelId)) {
        const modelPriceSum = model.Artikelnummer.reduce((sum, artikel) => {
          const price = Number(artikel.Grundpris);
          return sum + (isNaN(price) ? 0 : price);
        }, 0);

        const filterBedPrice = product.recommendedFilterBed && product.recommendedFilterBed.totalPrice ? product.recommendedFilterBed.totalPrice : 0;

        const controllerPrice = model.Styrenheter && selectedControllers[modelId]
          ? model.Styrenheter.filter(controller => controller.Artikelnummer === selectedControllers[modelId])
            .reduce((ctrlSum, controller) => ctrlSum + Number(controller.Grundpris || 0), 0)
          : 0;

        const optionalPriceSum = model.Tillval ? model.Tillval.reduce((sum, optional) => {
          const optionalId = optional['Art.nr.'];
          if (selectedOptionals[modelId] === optionalId) {
            return sum + optional.Pris;
          }
          return sum;
        }, 0) : 0;

        return modelSum + modelPriceSum + filterBedPrice + controllerPrice + optionalPriceSum;
      }
      return modelSum;
    }, 0);
    return acc + productPriceSum;
  }, 0);

  const logoSrc = "AquaExpert2024_blue_lay.png";

  const toggleModelSelection = (modelId, isSelected) => {
    setSelectedModelIds(currentSelectedModelIds => {
      if (isSelected) {
        const updatedModelIds = [...currentSelectedModelIds, modelId];
        ensureControllerIsSet(modelId);
        return updatedModelIds;
      } else {
        return currentSelectedModelIds.filter(id => id !== modelId);
      }
    });
  };

  const ensureControllerIsSet = (modelId) => {
    const model = products.flatMap(product => product.Modeller).find(m => `${m.Product}-${m.Modell}` === modelId);
    if (model && !selectedControllers[modelId]) {
      if (model.Styrenheter && model.Styrenheter.length > 0) {
        handleControllerChange(modelId, model.Styrenheter[0].Artikelnummer);
      }
    }
  };

  const validProducts = products.filter(product =>
    product.Product === "Alterline" || product.Modeller.some(model => getModelFlowCapacity(model.Specifikationer, likelyFlow, analysisData))
  );

  const handleCustomerInfoChange = (field, value) => {
    setCustomerInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleResellerInfoChange = (field, value) => {
    setResellerInfo(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Card style={{ padding: '20px' }}>
      <Typography.Title level={4}>Rekommenderade produkter</Typography.Title>
      <Row gutter={[16, 16]}>
        {validProducts.map((product) => {
          const currentProductProblems = presentProblems.find(p => p.Product === product.Product)?.Problemlösning || [];

          return product.Modeller.map((model, modelIndex) => {
            const modelId = `${product.Product}-${model.Modell}-${modelIndex}`;
            const isSelected = selectedModelIds.includes(modelId);
            const operatingCosts = calculateAnnualOperatingCosts(model, likelyFlow, totalHardness, nitrateLevel);
            const annualSaltCost = operatingCosts.annualSaltCost?.toFixed(2).toLocaleString('sv-SE') || '0.00';

            return (
              <Col span={8} key={modelId}>
                <div style={{ display: 'flex', flexDirection: 'column', padding: '10px', border: '1px solid #f0f0f0', borderRadius: '8px', marginTop: '10px' }}>
                  <div style={{ fontWeight: 'bold' }}>{product.Product}</div>
                  {'Aktuell problemlösning: '}
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {product.Product === "Alterline" && !currentProductProblems.some(prob => prob.name === 'pH') && (
                      <Tag color={colorMap['pH']} style={{ padding: '2px 6px', borderRadius: '4px', marginRight: '4px', marginBottom: '4px' }}>
                        pH
                      </Tag>
                    )}
                    {currentProductProblems.map((prob, idx) => (
                      <Tag key={idx} color={colorMap[prob.name]} style={{ padding: '2px 6px', borderRadius: '4px', marginRight: '4px', marginBottom: '4px' }}>
                        {prob.name}
                      </Tag>
                    ))}
                  </div>
                  <Checkbox
                    onChange={(e) => toggleModelSelection(modelId, e.target.checked)}
                    checked={isSelected}
                    style={{ marginBottom: '10px' }}
                  >
                    <strong>Modell:</strong> {model.Modell}
                  </Checkbox>
                  {model.Artikelnummer.map((artikel, artikelIndex) => (
                    <div key={`${artikel['Art.nr.']}-${artikelIndex}`} style={{ marginBottom: '5px' }}>
                      <div>Artikelnummer: {artikel['Art.nr.']}</div>
                      <div>Pris: {artikel.Grundpris ? artikel.Grundpris.toLocaleString('sv-SE') + ' SEK' : 'Pris ej tillgängligt'}</div>
                    </div>
                  ))}
                  {model.Styrenheter && model.Styrenheter.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <strong style={{ marginRight: '5px' }}>Styrenhet:</strong>
                        {model.Styrenheter.length > 1 ? (
                          <Select
                            value={selectedControllers[modelId] || model.Styrenheter[0].Artikelnummer}
                            style={{ width: '70%' }}
                            onChange={(value) => handleControllerSelection(modelId, value)}
                          >
                            {model.Styrenheter.map(controller => (
                              <Option key={controller.Artikelnummer} value={controller.Artikelnummer}>
                                {controller.name}
                              </Option>
                            ))}
                          </Select>
                        ) : (
                          <span>{model.Styrenheter[0].name}</span>
                        )}
                      </div>
                      <div>
                        <div>Artikelnummer: {selectedControllers[modelId]
                          ? model.Styrenheter.find(controller => controller.Artikelnummer === selectedControllers[modelId]).Artikelnummer
                          : model.Styrenheter[0].Artikelnummer}
                        </div>
                        <div>Pris: {selectedControllers[modelId]
                          ? model.Styrenheter.find(controller => controller.Artikelnummer === selectedControllers[modelId]).Grundpris.toLocaleString('sv-SE')
                          : model.Styrenheter[0].Grundpris.toLocaleString('sv-SE')} SEK
                        </div>
                      </div>
                    </div>
                  )}
                  {model.Tillval && model.Tillval.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
                      <strong>Tillval:</strong>
                      {model.Tillval.length > 1 ? (
                        <>
                          <Select
                            value={selectedOptionals[modelId] || 'none'}
                            onChange={(value) => handleOptionalSelection(modelId, value)}
                            style={{ width: '100%' }}
                          >
                            <Option key="none" value="none">Inget</Option>
                            {model.Tillval.map(optional => (
                              <Option key={optional['Art.nr.']} value={optional['Art.nr.']}>
                                {optional.Produkt}
                              </Option>
                            ))}
                          </Select>
                          {selectedOptionals[modelId] && selectedOptionals[modelId] !== 'none' && (
                            <div style={{ marginTop: '10px' }}>
                              <div>Artikelnummer: {selectedOptionals[modelId]}</div>
                              <div>
                                Pris: {model.Tillval.find(optional => optional['Art.nr.'] === selectedOptionals[modelId]).Pris.toLocaleString('sv-SE')} SEK
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <Checkbox
                            onChange={() => handleOptionalSelection(modelId, selectedOptionals[modelId] === model.Tillval[0]['Art.nr.'] ? 'none' : model.Tillval[0]['Art.nr.'])}
                            checked={selectedOptionals[modelId] === model.Tillval[0]['Art.nr.']}
                            style={{ marginBottom: '5px' }}
                          >
                            {model.Tillval[0].Produkt}
                          </Checkbox>
                          {selectedOptionals[modelId] === model.Tillval[0]['Art.nr.'] && (
                            <>
                              <div>Artikelnummer: {model.Tillval[0]['Art.nr.']}</div>
                              <div>Pris: {model.Tillval[0].Pris.toLocaleString('sv-SE')} SEK</div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {annualSaltCost !== '0.00' && <div>Årlig saltkostnad: {annualSaltCost} SEK</div>}
                  {model.Filterbäddar && model.Filterbäddar.length > 0 && product.Product === "Crystal Right (CR)" && (
                    <div>
                      {model.Filterbäddar.map((index) => (
                        <div key={`filterBed-${index}-${modelId}`}>
                          {model.Extra_Problemlösning && model.Extra_Problemlösning.length > 0 && (
                            <div>
                              {'Extra problemlösning: '}
                              {model.Extra_Problemlösning.map((extraProb, index) => (
                                <Tag color="green" key={`extra-${index}`}>{extraProb.name}</Tag>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {product.recommendedFilterBed && (
                    <div>
                      <strong>Filterbädd:</strong> {product.recommendedFilterBed.quantity && `${product.recommendedFilterBed.quantity}x `}{product.recommendedFilterBed.name}
                      <div>Artikelnummer: {product.recommendedFilterBed.Artikelnummer}</div>
                      <div>Totalpris för filterbäddar: {product.recommendedFilterBed.totalPrice ? product.recommendedFilterBed.totalPrice.toLocaleString('sv-SE') + ' SEK' : 'Pris ej tillgängligt'}</div>
                    </div>
                  )}
                </div>
              </Col>
            );
          });
        })}
      </Row>
      <Typography.Text style={{ fontWeight: 'bold' }}>
        TOTAL SUMMA: {totalSum.toLocaleString('sv-SE')} SEK
      </Typography.Text>
      <Divider />
      <Title level={4}>Återförsäljare</Title>
      <Input
        placeholder="Förnamn Efternamn"
        value={resellerInfo.name}
        onChange={e => handleResellerInfoChange('name', e.target.value)}
        style={{ marginBottom: '10px', width: '80%' }}
      />
      <br />
      <Input
        placeholder="Adress postnummer Ort"
        value={resellerInfo.address}
        onChange={e => handleResellerInfoChange('address', e.target.value)}
        style={{ marginBottom: '15px', width: '80%' }}
      />
      <Title level={4}>Slutkund</Title>
      <Input
        placeholder="Förnamn Efternamn"
        value={customerInfo.name}
        onChange={e => handleCustomerInfoChange('name', e.target.value)}
        style={{ marginBottom: '10px', width: '80%' }}
      />
      <br />
      <Input
        placeholder="Adress postnummer Ort"
        value={customerInfo.address}
        onChange={e => handleCustomerInfoChange('address', e.target.value)}
        style={{ marginBottom: '15px', width: '80%' }}
      />
      <PDFViewer style={{ width: '100%', height: '1250px', marginTop: '20px' }}>
        <OfferDocument
          products={products.map(product => {
            const currentProductProblems = presentProblems.find(p => p.Product === product.Product)?.Problemlösning || [];
            const adjustedProblemlösning = product.Product === "Alterline" && !currentProductProblems.some(prob => prob.name === 'pH')
              ? [...currentProductProblems, { name: 'pH' }]
              : currentProductProblems;

            return {
              ...product,
              Modeller: product.Modeller.filter(model => {
                const modelIdWithIndex = `${product.Product}-${model.Modell}-${product.Modeller.indexOf(model)}`;
                return selectedModelIds.includes(modelIdWithIndex);
              }).map((model, modelIndex) => ({
                ...model,
                problemlösning: adjustedProblemlösning,
                modelIndex,
                product: product.Product,
                selectedController: selectedControllers[`${product.Product}-${model.Modell}-${modelIndex}`],
                selectedOptional: selectedOptionals[`${product.Product}-${model.Modell}-${modelIndex}`]
              }))
            };
          }).filter(product => product.Modeller.length > 0)}
          customerInfo={customerInfo}
          resellerInfo={resellerInfo}
          selectedControllers={selectedControllers}
          selectedOptionals={selectedOptionals}
          logoSrc={logoSrc}
        />
      </PDFViewer>
      <PDFDownloadLink
        document={<OfferDocument
          products={products.map(product => {
            const currentProductProblems = presentProblems.find(p => p.Product === product.Product)?.Problemlösning || [];
            const adjustedProblemlösning = product.Product === "Alterline" && !currentProductProblems.some(prob => prob.name === 'pH')
              ? [...currentProductProblems, { name: 'pH' }]
              : currentProductProblems;

            return {
              ...product,
              Modeller: product.Modeller.filter(model => {
                const modelIdWithIndex = `${product.Product}-${model.Modell}-${product.Modeller.indexOf(model)}`;
                return selectedModelIds.includes(modelIdWithIndex);
              }).map((model, modelIndex) => ({
                ...model,
                problemlösning: adjustedProblemlösning,
                modelIndex,
                product: product.Product,
                selectedController: selectedControllers[`${product.Product}-${model.Modell}-${modelIndex}`],
                selectedOptional: selectedOptionals[`${product.Product}-${model.Modell}-${modelIndex}`]
              }))
            };
          }).filter(product => product.Modeller.length > 0)}
          customerInfo={customerInfo}
          resellerInfo={resellerInfo}
          selectedControllers={selectedControllers}
          selectedOptionals={selectedOptionals}
          logoSrc={logoSrc}
        />}
        fileName={`offert-${Date.now()}.pdf`}
      >
        {({ loading }) => (
          <Button
            type="primary"
            disabled={loading}
            icon={<DownloadOutlined />}
            style={{ marginTop: '20px' }}
          >
            {loading ? 'Laddar' : 'Ladda ner offert'}
          </Button>
        )}
      </PDFDownloadLink>
    </Card>
  );
};

export default Quote;
